import { useState, useEffect, useMemo, useContext, useCallback } from 'react';
import {
    Modal, ModalDialog, ModalClose, Typography, Box, Button,
    Input, Select, Option, IconButton, Stack, CircularProgress, Chip,
} from '@mui/joy';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import { AppContext } from '../context';
import { APIGetProducts, APICreateWriteOffRequest, parseApiError } from '../api';

const REASON_PRESETS = ['Сломалось', 'Утеряно', 'Брак', 'Пересорт'];

const fmtQty = (v) => {
    const n = Number(v);
    if (!isFinite(n)) return v;
    return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, '').replace('.', ',');
};
const sanitizeQty = (raw) => {
    let s = raw.replace(/[^\d.,]/g, '').replace(/,/g, '.');
    const i = s.indexOf('.');
    if (i !== -1) s = s.slice(0, i + 1) + s.slice(i + 1).replace(/\./g, '');
    return s.replace('.', ',');
};

export default function WriteOffRequestModal({ open, onClose, onDone }) {
    const { token, showToast } = useContext(AppContext);
    const [products, setProducts] = useState([]);
    const [loading,  setLoading]  = useState(false);
    const [rows,     setRows]     = useState([{ product_id: '', quantity: '' }]);
    const [reason,   setReason]   = useState('');
    const [saving,   setSaving]   = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        const data = await APIGetProducts(token, { is_active: true });
        setProducts(Array.isArray(data) ? data : []);
        setLoading(false);
    }, [token]);

    useEffect(() => {
        if (open) { load(); setRows([{ product_id: '', quantity: '' }]); setReason(''); }
    }, [open, load]);

    const usedIds = useMemo(() => new Set(rows.map(r => r.product_id).filter(Boolean)), [rows]);
    const setRow = (i, f, v) => setRows(prev => prev.map((r, idx) => idx === i ? { ...r, [f]: v } : r));
    const addRow = () => setRows(prev => [...prev, { product_id: '', quantity: '' }]);
    const delRow = (i) => setRows(prev => prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev);

    const handleSubmit = async () => {
        const items = rows
            .filter(r => r.product_id && parseFloat(String(r.quantity).replace(',', '.')) > 0)
            .map(r => ({ product_id: Number(r.product_id), quantity: String(r.quantity).replace(',', '.') }));
        if (items.length === 0) { showToast('Заполните хотя бы одну позицию', 'warning'); return; }
        if (reason.trim().length < 3) { showToast('Укажите причину списания', 'warning'); return; }

        setSaving(true);
        const res = await APICreateWriteOffRequest(token, { comment: reason.trim(), items });
        setSaving(false);
        if (res?.error) { showToast(parseApiError(res.error, 'Не удалось создать заявку'), 'danger'); return; }
        showToast('Заявка на списание отправлена на согласование ✓', 'success');
        onDone?.(res);
        onClose();
    };

    return (
        <Modal open={open} onClose={onClose}>
            <ModalDialog sx={{
                bgcolor: 'var(--joy-palette-background-level1)',
                border: '1px solid var(--joy-palette-divider)',
                borderRadius: 'var(--joy-radius-xl)',
                maxWidth: 460, width: '100%', p: { xs: 2, sm: 3 },
                maxHeight: '95vh', overflowY: 'auto',
            }}>
                <ModalClose />
                <Typography level="h4" sx={{ fontWeight: 800, mb: 0.3, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Inventory2RoundedIcon /> Списать не бумагу
                </Typography>
                <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-secondary)', mb: 1.5 }}>
                    Заявка уйдёт руководителю. Склад уменьшится только после одобрения.
                    Дробное — через запятую (1,8).
                </Typography>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress /></Box>
                ) : products.length === 0 ? (
                    <Box sx={{
                        textAlign: 'center', py: 3, px: 2, mb: 1.5,
                        borderRadius: 'var(--joy-radius-md)',
                        border: '1px dashed var(--joy-palette-divider)',
                    }}>
                        <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-tertiary)' }}>
                            Нет материалов для списания. Отметьте нужную категорию флагом
                            «Свободное списание (бумага)» в разделе «Товары».
                        </Typography>
                    </Box>
                ) : (
                    <Stack spacing={1} sx={{ mb: 1.5 }}>
                        {rows.map((r, i) => (
                            <Stack key={i} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                <Select size="sm" value={r.product_id} placeholder="Материал"
                                    onChange={(_, v) => setRow(i, 'product_id', v || '')} sx={{ flex: 1 }}
                                    slotProps={{
                                        listbox: {
                                            sx: {
                                                zIndex: 1400,   // выше модалки — список не прячется
                                                bgcolor: 'var(--joy-palette-background-level1)',
                                                border: '1px solid var(--joy-palette-divider)',
                                                color: 'var(--joy-palette-text-primary)',
                                                boxShadow: 'lg',
                                                '& .MuiOption-root:hover': {
                                                    bgcolor: 'var(--joy-palette-background-level2)',
                                                },
                                                '& .MuiOption-root[aria-selected="true"]': {
                                                    bgcolor: 'rgba(162,38,226,0.16)',
                                                },
                                            },
                                        },
                                    }}
                                >
                                    {products
                                        .filter(p => !usedIds.has(String(p.id)) || String(p.id) === r.product_id)
                                        .map(p => (
                                            <Option key={p.id} value={String(p.id)}>
                                                {p.name} <span style={{ opacity: 0.6 }}>· ост. {p.is_unlimited ? '∞' : fmtQty(p.stock)}</span>
                                            </Option>
                                        ))}
                                </Select>
                                <Input size="sm" inputMode="decimal" placeholder="−кол-во"
                                    value={r.quantity}
                                    onChange={(e) => setRow(i, 'quantity', sanitizeQty(e.target.value))}
                                    sx={{ width: 100 }} />
                                {rows.length > 1 && (
                                    <IconButton size="sm" variant="plain" color="danger" onClick={() => delRow(i)}>
                                        <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                )}
                            </Stack>
                        ))}
                        <Button size="sm" variant="plain" startDecorator={<AddRoundedIcon sx={{ fontSize: 16 }} />}
                            onClick={addRow} sx={{ alignSelf: 'flex-start', fontWeight: 700 }}>
                            Добавить позицию
                        </Button>
                    </Stack>
                )}

                <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', mb: 0.6 }}>
                    Причина списания *
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, mb: 1 }}>
                    {REASON_PRESETS.map(rp => (
                        <Chip key={rp} size="sm" variant="soft"
                            color={reason === rp ? 'primary' : 'neutral'}
                            onClick={() => setReason(rp)}
                            sx={{ cursor: 'pointer', fontWeight: 700 }}>
                            {rp}
                        </Chip>
                    ))}
                </Box>
                <Input size="sm" placeholder="Опишите причину (сломалось, утеряно, брак…)"
                    value={reason} onChange={(e) => setReason(e.target.value)} sx={{ mb: 1.5 }} />

                <Button color="warning" onClick={handleSubmit} loading={saving} sx={{ fontWeight: 800 }}>
                    Отправить на согласование
                </Button>
            </ModalDialog>
        </Modal>
    );
}